/**
 * Auth Session Guard
 *
 * Two pieces of the Supabase auth plumbing that must not be inlined into the
 * client factory, because they are the difference between "one tab lost a
 * refresh race" and "every tab is signed out".
 *
 * With `persistSession` + `autoRefreshToken`, every open tab schedules its own
 * refresh against the one shared `sb-*-auth-token`. Refresh coalescing inside
 * Supabase is per client, so only a cross-tab lock stops a tab refreshing with a
 * token other tabs have already replaced. The server forgives a token one
 * replacement behind, since the client may have missed the answer, but refuses
 * one two behind, such as a tab frozen across two refreshes. Two things then end
 * the session for every tab: auth-js removes the stored session on any
 * non-retryable refresh failure, and the fetch interceptor in supabaseClient.ts
 * wiped it on any 400 from `/auth/v1/token`.
 *
 * Seen in production on 2026-10-30: a token rotated at 07:09:46, two requests
 * sent the old one at 07:16:37 (`refresh_token_already_used`), and the account
 * was logged out at 07:16:38.
 *
 * So: (1) serialize auth work across tabs, and (2) when a refresh does lose a
 * race, notice that another tab already rotated the token and leave it alone.
 */

/** Shape of the session blob Supabase persists under `sb-<ref>-auth-token`. */
interface StoredSessionLike {
  refresh_token?: unknown;
}

/** Supabase prefixes base64-encoded session blobs with this marker. */
const BASE64_PREFIX = 'base64-';

/**
 * True when this request is a refresh-grant call — the only token-endpoint
 * traffic whose failure says anything about the stored session.
 *
 * A password grant that 400s means "you typed the wrong password", which must
 * never touch an existing session: before this check, a failed sign-in in one
 * tab logged the user out of every other tab.
 */
export function isRefreshGrantRequest(url: string): boolean {
  return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');
}

/** Pull the refresh token out of an outgoing refresh-grant request body. */
export function extractSentRefreshToken(body: unknown): string | null {
  if (typeof body !== 'string') return null;
  try {
    const parsed = JSON.parse(body) as { refresh_token?: unknown };
    return typeof parsed.refresh_token === 'string' && parsed.refresh_token
      ? parsed.refresh_token
      : null;
  } catch {
    return null;
  }
}

/** Decode one persisted session blob, tolerating the base64 envelope. */
function decodeStoredSession(raw: string): StoredSessionLike | null {
  let text = raw;
  if (text.startsWith(BASE64_PREFIX)) {
    try {
      text = atob(text.slice(BASE64_PREFIX.length));
    } catch {
      return null;
    }
  }
  try {
    return JSON.parse(text) as StoredSessionLike;
  } catch {
    return null;
  }
}

/** Every refresh token currently persisted by Supabase in this storage. */
export function readStoredRefreshTokens(storage: Storage): string[] {
  const tokens: string[] = [];
  let keys: string[];
  try {
    keys = Object.keys(storage);
  } catch {
    return tokens;
  }
  for (const key of keys) {
    if (!key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      continue;
    }
    if (!raw) continue;
    const session = decodeStoredSession(raw);
    const token = session?.refresh_token;
    if (typeof token === 'string' && token) tokens.push(token);
  }
  return tokens;
}

/** All `sb-*-auth-token` keys, i.e. the ones a teardown should remove. */
export function readStoredSessionKeys(storage: Storage): string[] {
  try {
    return Object.keys(storage).filter(
      (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
    );
  } catch {
    return [];
  }
}

/**
 * Decide whether a 400 on a refresh grant means the stored session is really
 * dead, or just that another tab got there first.
 *
 * The discriminator is whether storage still holds the token we just failed
 * with. If it holds a *different* token, a concurrent tab already rotated
 * successfully and that new token is valid — destroying it would sign out a
 * user whose session is perfectly healthy.
 */
export function shouldClearStoredSession(
  sentRefreshToken: string | null,
  storedRefreshTokens: string[]
): boolean {
  // Nothing persisted — nothing to tear down.
  if (storedRefreshTokens.length === 0) return false;
  // Body unreadable: fall back to the conservative original behaviour and clear,
  // so a genuinely expired token can never wedge sign-in.
  if (!sentRefreshToken) return true;
  // Storage still holds the token that just failed => it is genuinely dead.
  return storedRefreshTokens.includes(sentRefreshToken);
}

// ── Cross-tab auth lock ───────────────────────────────────────────────────────

/**
 * Longest we will wait behind another tab's auth operation before going ahead
 * without the lock. Auth calls take milliseconds, so this only fires when a tab
 * froze or died holding it; a rare unserialized refresh beats a hung app, and
 * shouldClearStoredSession covers the race that can follow.
 */
const LOCK_SAFETY_TIMEOUT_MS = 10_000;

/** In-process fallback chain for environments without the Web Locks API. */
let fallbackChain: Promise<unknown> = Promise.resolve();

function hasWebLocks(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { locks?: unknown }).locks !== 'undefined'
  );
}

/**
 * An auth lock that serializes across tabs.
 *
 * Supabase's own `navigatorLock` aborts on `acquireTimeout`, which produced the
 * "Lock was stolen" AbortErrors that once got it replaced with a no-op. This one
 * ignores `acquireTimeout` and queues, so concurrent `getSession()` and refresh
 * calls wait their turn and never throw at the caller. A queued request that
 * outlives LOCK_SAFETY_TIMEOUT_MS is withdrawn before `fn` runs without the lock,
 * so `fn` can never run twice.
 */
export async function crossTabAuthLock<R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  if (!hasWebLocks()) {
    // Serialize in-process so at least same-tab concurrency is ordered.
    const run = fallbackChain.then(fn, fn);
    fallbackChain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  const locks = (navigator as Navigator & { locks: LockManager }).locks;
  const controller = new AbortController();
  let started = false;
  const timer = setTimeout(() => {
    if (!started) controller.abort();
  }, LOCK_SAFETY_TIMEOUT_MS);

  try {
    return (await locks.request(name, { mode: 'exclusive', signal: controller.signal }, () => {
      started = true;
      return fn();
    })) as R;
  } catch (error) {
    if (started) throw error;
    console.warn(`[Supabase] Could not get auth lock "${name}"; proceeding without it`);
    return fn();
  } finally {
    clearTimeout(timer);
  }
}
